/**
 * One error type for the whole app.
 *
 * The old app threw three different shapes (raw AxiosError, a custom
 * ServiceError, and a bare Error from an interceptor), so every catch block
 * had to guess. Here everything the API layer rejects with is an ApiError.
 */
export class ApiError extends Error {
  readonly status: number;
  /** Server error code, e.g. "forbidden" | "not_found" | "conflict". */
  readonly code: string;
  /** Raw response body, for debugging. Never render this. */
  readonly body: unknown;

  constructor(message: string, status: number, code: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.body = body;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
  get isForbidden(): boolean {
    return this.status === 403;
  }
  /** 409 — e.g. deleting a group that still has organizations. */
  get isConflict(): boolean {
    return this.status === 409;
  }
  /** 422 — the frontend sent a malformed body (FastAPI validation). */
  get isValidation(): boolean {
    return this.status === 422;
  }
}

/**
 * Thrown when a response is shaped differently than the schema expects.
 *
 * This is the fix for the single most expensive bug class in the previous app:
 * `response.data.data as T` silently produced `undefined` when a field was
 * renamed server-side (user→username, successful_logins→events,
 * latest_version→latest_versions), and the UI just rendered blank. Now it
 * fails loudly, at the boundary, naming the exact field.
 */
export class SchemaError extends Error {
  readonly endpoint: string;
  readonly issues: string[];

  constructor(endpoint: string, issues: string[]) {
    super(
      `API response did not match the expected shape for ${endpoint}:\n` +
        issues.map((i) => `  • ${i}`).join("\n"),
    );
    this.name = "SchemaError";
    this.endpoint = endpoint;
    this.issues = issues;
  }
}

/** Narrow an unknown catch value to a displayable message. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError || e instanceof SchemaError) return e.message;
  if (e instanceof Error) return e.message;
  return "Something went wrong";
}
